import { useState, useMemo, useEffect, forwardRef } from "react";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import { Row } from "react-bootstrap";
import { GlobalState, useStateMachine } from "little-state-machine";
import { useNavigate } from "react-router-dom";
import { useColumnOrder, useExpanded, useSortBy, useTable } from "react-table";

import "../../shipper/Quotes/Quotes.scss";
import { getQuotations } from "api";
import { useGetAssignedTeams } from "api/queries/quotation/useGetAssignedTeams";
import Card from "components/molecules/Card/Card";
import Button from "components/atoms/Button/Button";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import DataTable from "components/organisms/DataTable/DataTable";
import { ReactComponent as EyeIcon } from "assets/images/icons/Eye.svg";
import SubQuote from "pages/shipper/ShipmentQuotes/components/quotes/SubQuote";
import { IQuotation } from "pages/shipper/ShipmentQuotes/components/quotes/QuotesType";
import RateCardErrors from "pages/shipper/ShipmentQuotes/components/RateCardErrors";
import ParcelQuoteErrors from "pages/shipper/ShipmentQuotes/components/ParcelQuoteErrors";
import { useQuotationLists } from "api/queries/quotation/useQuotationLists";
import QuotationDetailTable from "./QuotationDetailTable";
import QuotesDetailTab from "./QuotesDetailTab";
import getQuotationListColumn from "./QuotationListColumn";
import { IQuotationListProps } from "./QuotationType";

const SHIPMENT_STATUS = ["Delayed", "In transit", "Incomplete", "Delivered", "Auto-closed", "Booked", "Cancelled"];

const QuotationList = forwardRef<HTMLDivElement, IQuotationListProps>((props, ref) => {
  const { id, providers, quoteDetailData, setShowTab, showTab, shipmentData, shipmentBasicError } = props || {};
  const navigate = useNavigate();

  const [allQuotes, setAllQuotes] = useState<IQuotation[]>([]);
  const [data, setData] = useState<IQuotation[]>([]);
  const [showAlternativeQuotes, setShowAlternativeQuotes] = useState(false);
  const [alternativeQuotesEnabled, setAlternativeQuotesEnabled] = useState(false);

  const intl = useIntl();
  const { actions } = useStateMachine({ updateQuotationsAvailable, updateRequest }) as any;
  const { quoting_deadline_seconds_remaining, request_status } = quoteDetailData?.organization || {};
  const isQuotingDeadlineExpired = quoting_deadline_seconds_remaining <= 0;
  const { related_shipments } = shipmentData || {};
  const shipmentRelation = related_shipments && related_shipments[0]?.relation;
  const isShipmentBooked = SHIPMENT_STATUS.includes(shipmentData?.shipment?.request_status);
  const isUninvited = request_status === "Uninvited";

  // If pull_quotes = true then we want to pull quotes every 30 seconds for 2.5 minutes
  // this allows for us to pull the parcel & rate card quotes that will be generated after the quote request is created
  const urlParams = new URLSearchParams(window.location.search);
  const quotationReference = urlParams.get("quotation_reference");

  const quotesTab = [
    { title: "Quotes", eventKey: "quotes" },
    { title: "Details", eventKey: "detail" },
  ];

  const { data: assignedTeams = [] } = useGetAssignedTeams({
    id: id!,
    config: { params: { already_assigned: true } },
  });

  const {
    data: quotesData,
    isFetching,
    refetch: refetchQuotationLists,
  } = useQuotationLists({
    shipmentId: id,
    queryFn: () => getQuotations(id),
    options: {
      enabled: !!id,
      refetchOnMount: false,
    },
  });

  const columns = useMemo(() => getQuotationListColumn(intl, refetchQuotationLists, shipmentData), [shipmentData]);

  const instance = useTable<any>(
    {
      columns,
      data,
      SubQuote,
    },
    useSortBy,
    useColumnOrder,
    useExpanded,
  );

  const fallbackMessage = "No quotes found.";

  useEffect(() => {
    refetchQuotationLists();
  }, [id]);

  useEffect(() => {
    if (quotesData?.data.quotations.length > 0) {
      const quotationData = quotesData.data;
      const formattedData = formatData(quotationData.quotations);
      setAllQuotes(formattedData);
      const requireApprovalQuotes = quotationData.quotations.filter(
        (quote: IQuotation) => quote.status === "require_approval",
      );
      if (requireApprovalQuotes.length > 0 && quotationData.quotations.length > 1) {
        setAlternativeQuotesEnabled(true);
        setData(formatData(requireApprovalQuotes));
      } else {
        setData(formattedData);
        setAlternativeQuotesEnabled(false);
      }
      actions.updateRequest({ isupdateRequest: false });
    }
  }, [quotesData]);

  useEffect(() => {
    if (showAlternativeQuotes) setData(allQuotes);
    else setData(allQuotes.filter((quote: IQuotation) => quote.status === "require_approval"));
  }, [showAlternativeQuotes]);

  useEffect(() => {
    if (!data || data.length === 0) actions.updateQuotationsAvailable(false);
    else actions.updateQuotationsAvailable(true);
  }, [data]);

  useEffect(() => {
    if (quotationReference && instance) {
      const row = instance.rows.find((r) => r.original.reference === quotationReference);
      if (row) row.toggleRowExpanded();
    }
  }, [quotationReference, instance]);

  function updateRequest(state: GlobalState, payload: { isupdateRequest: boolean }) {
    return {
      ...state,
      isupdateRequest: payload.isupdateRequest,
    };
  }

  const handleStatusChange = (eventKey: string) => {
    setShowTab(eventKey);
  };

  function updateQuotationsAvailable(state: GlobalState, payload: any) {
    return {
      ...state,
      quotationsAvailable: payload,
    };
  }

  const formatData = (quoteData: IQuotation[]) => {
    const earliestDeliveryDate = moment.min(
      quoteData
        .filter((item) => item.schedule.quoted_time_delivery_date)
        .map((item) => moment(item.schedule.quoted_time_delivery_date)),
    );
    const earliestPickupDate = moment.min(quoteData.map((item) => moment(item.schedule.quoted_pickup_date)));

    // If ALL pickupdates are the same, then no pickup date is the earliest. Otherwise it sets an earliest key.
    if (!quoteData.every((item) => moment(item.schedule.quoted_pickup_date).isSame(earliestPickupDate))) {
      quoteData.forEach((item) => {
        if (moment(item.schedule.quoted_pickup_date).isSame(earliestPickupDate)) item.schedule.earliest_pickup = true;
      });
    }

    // If ALL delivery dates are the same, then no delivery date is the earliest. Otherwise it sets an earliest key.
    if (!quoteData.every((item) => moment(item.schedule.quoted_time_delivery_date).isSame(earliestDeliveryDate))) {
      quoteData.forEach((item) => {
        if (moment(item.schedule.quoted_time_delivery_date).isSame(earliestDeliveryDate))
          item.schedule.earliest_delivery = true;
      });
    }

    if (
      !quoteData.every((item) => moment(item.schedule.quoted_pickup_date).isSame(earliestPickupDate)) ||
      !quoteData.every((item) => moment(item.schedule.quoted_time_delivery_date).isSame(earliestDeliveryDate))
    ) {
      quoteData.forEach((item: IQuotation) => {
        const transitTime = moment(item.schedule.quoted_time_delivery_date).diff(
          moment(item.schedule.quoted_pickup_date),
          "hours",
        );
        item.schedule.transit_time = transitTime;
      });
      const shortestTransitTime = Math.min(...quoteData.map((item) => item.schedule.transit_time));
      quoteData.forEach((item) => {
        if (item.schedule.transit_time === shortestTransitTime) item.schedule.shortest_transit_time = true;
      });
    }

    // Set price labels, only if every single quote has the same quote price, it gets no price labels.
    if (!quoteData.every((item) => item.financial.total_quoted_amount === quoteData[0].financial.total_quoted_amount)) {
      quoteData.sort((a: any, b: any) => a.financial.total_quoted_amount - b.financial.total_quoted_amount);

      quoteData.forEach((item: any) => {
        const highestPrice = Math.max(...quoteData.map((item: any) => item.financial.total_quoted_amount));
        const lowestPrice = Math.min(...quoteData.map((item: any) => item.financial.total_quoted_amount));
        if (highestPrice % 1 === 0) item.financial.highest_price = `${highestPrice}.00`;
        if (lowestPrice % 1 === 0) item.financial.lowest_price = `${lowestPrice}.00`;
        if (parseInt(item.financial.total_quoted_amount) === highestPrice) {
          item.financial.is_highest_price = true;
        }
        if (parseInt(item.financial.total_quoted_amount) === lowestPrice) {
          item.financial.is_lowest_price = true;
        }
      });
    }
    return quoteData;
  };

  const handleRowClick = (row: any, event: any) => {
    const modalWrapper = document.getElementsByClassName("modal")[0];
    if (modalWrapper && modalWrapper.contains(event.target)) return;
    event.stopPropagation();
    if (event.target.tagName === "BUTTON") return;
    row.toggleRowExpanded();
  };

  const handleRedirectQuotationform = () => {
    navigate(`/shipment/${id}/quotation/add`);
  };

  return (
    <div ref={ref}>
      {providers && <RateCardErrors providers={providers} />}
      {providers && <ParcelQuoteErrors providers={providers} />}
      <Row className="mx-0" style={{ marginBottom: "16px" }}>
        <Card cardStyle={{ borderRadius: "16px", cursor: "default !important" }} className="quotes-container">
          <div className="p-4">
            {data?.length > 0 && (
              <p className="d-flex justify-content-between">
                <PillTabs id="quote-statuses" activeKey={showTab} onSelect={handleStatusChange} tabItems={quotesTab} />
                {showTab === "detail" && <QuotesDetailTab quoteDetailData={quoteDetailData} />}
              </p>
            )}
          </div>

          {showTab === "quotes" ? (
            <>
              {data?.length === 0 && !isFetching && !isQuotingDeadlineExpired ? (
                <div className="pl-4 pb-4">
                  <p className="text-secondary">
                    <FormattedMessage id="quote.noQuoteSubmitted" defaultMessage="No quotes submitted yet" />
                  </p>
                  {assignedTeams.length > 0 &&
                    shipmentBasicError?.response?.status !== 403 &&
                    !isShipmentBooked &&
                    !isUninvited && (
                      <Button variant="primary" onClick={handleRedirectQuotationform}>
                        <FormattedMessage id="quote.submitFirstQuote" defaultMessage="Submit first quote" />
                      </Button>
                    )}
                </div>
              ) : (
                <DataTable
                  customNoResults={fallbackMessage}
                  tableInstance={instance}
                  isLoading={isFetching}
                  RowSubComponent={SubQuote}
                  onRowClick={handleRowClick}
                />
              )}
            </>
          ) : (
            <QuotationDetailTable quoteDetailData={quoteDetailData} />
          )}
          {data?.length > 0 &&
            !isShipmentBooked &&
            !isQuotingDeadlineExpired &&
            showTab === "quotes" &&
            shipmentBasicError?.response?.status !== 403 &&
            !isUninvited &&
            (!related_shipments?.length || shipmentRelation === "Child") && (
              <div className="pl-4">
                <Button
                  variant="primary"
                  className="rounded-pill blue-200 h-26 mb-3"
                  label={<FormattedMessage id="quote.addAnotherQuote" defaultMessage="Add another quote" />}
                  onClick={handleRedirectQuotationform}
                />
              </div>
            )}
          {alternativeQuotesEnabled && (
            <div
              className="d-flex cursor-pointer pb-2"
              style={{ marginLeft: "2rem" }}
              onClick={() => setShowAlternativeQuotes((prev) => !prev)}
            >
              <EyeIcon /> <p className="ml-2">{`${showAlternativeQuotes ? "Hide" : "Show"} alternative quotes`}</p>
            </div>
          )}
        </Card>
      </Row>
    </div>
  );
});

export default QuotationList;
